import React from "react";
import { InspirationGroup } from "../components/InspirationGroup";
import * as Commons from "oc-commons/Commons";
import {
  comparatorForSortierung,
  filterPathIntersectionCached,
} from "oc-app/metadata/GraphMetadata";
import { DialogContainer } from "oc-ui/dialogs/Dialogs";
import { Auswahl } from "../components/Auswahl";
import { ChatBot } from "../components/chatbot/ChatBot";
import { MAuswahlwert, MInspiration } from "../../metamodell";
import { PAuswahlwert, PInspiration } from "../../metamodell_paths";
import { I18N } from "oc-I18N";
import { PageButton } from "../components/PageButtons";

export type PageInitProps = {
  pageEnteredInSequence: boolean;
};

/**
 * Erstellt die Inspirationsseite aus der DB Tabelle "Inspiration"
 * der state.materialKategorie filtert die angezeigten InspirationsGruppen.
 * Die InspirationsGruppen sind eine eigene React Component mit dem angezeigten Inspirationsbild
 * als veränderlicher state.
 *
 * @export
 * @class Inspiration
 * @extends {React.Component}
 */
export class Inspiration extends React.Component {
  pageNeeds = {
    pageTypeForCssAnimation: 2,
  };

  materialKategorien: MAuswahlwert[] = [];

  state: {
    materialKategorie: MAuswahlwert;
    zaunart: MAuswahlwert;
  };

  constructor(props: {}) {
    super(props);

    const zaunart = this.getSelectedZaunart();

    this.materialKategorien = filterPathIntersectionCached("Inspiration.tsx:42")(
      zaunart,
      (p: PAuswahlwert) => p.Inspiration.MaterialKategorie
    );

    this.state = {
      materialKategorie: APP.richMetadata.getAuswahlWertVoreinstellung(this.materialKategorien),
      zaunart,
    };
  }

  getSelectedZaunart() {
    return APP.arrangement?.zaunart ?? APP.richMetadata.findAuswahlwert("Sichtschutz");
  }

  hasZaunartSelect() {
    return true;
  }

  init({ pageEnteredInSequence }: PageInitProps) {}

  async exit(newPageName: string) {
    const agt = APP.arrangement;
    const grundriss = agt.grundriss;
    if (grundriss.kanten.length == 0) {
      agt.verteilungsstrategieAuswahlwert_ = APP.richMetadata.getAuswahlWertVoreinstellung(
        APP.metadata.Verteilungsstrategie_Auswahlwert
      );
      grundriss.setStandardForm("Strecke");
      agt.update3d();
    }
    const füllung = agt.inspiration?.Füllung;
    if (füllung) {
      agt.changeSelectedZaunElemente({ füllungAuswahlwert: füllung });
    }
  }

  getDetailSerieInspirationen(): Map<MAuswahlwert, MInspiration[]> {
    const inspirationen: MInspiration[] = filterPathIntersectionCached("Inspiration.tsx:59")(
      this.state.zaunart,
      (p: PAuswahlwert) => p.Inspiration,
      this.state.materialKategorie,
      (p: PAuswahlwert) => p.Inspiration
    ).filter(
      (i) =>
        !(
          i.Inspiration?.Gesperrt ||
          i.DetailSerie?.Auswahlwert?.Gesperrt ||
          i.Zaunart?.Gesperrt ||
          (i.MaterialKategorie.length > 0 && i.MaterialKategorie.every((m) => m.Gesperrt)) ||
          i.FarbKategorie?.Gesperrt ||
          i.Mass?.Auswahlwert?.Gesperrt ||
          i.Füllung?.Gesperrt
        )
    );
    // sortiere keys und values nach Sortierung
    const map: Map<MAuswahlwert, MInspiration[]> = Commons.groupByFlat(
      inspirationen,
      (inspiration: MInspiration) => inspiration.DetailSerie.Auswahlwert
    );
    const sorted = new Map<MAuswahlwert, MInspiration[]>();
    Array.from(map.keys())
      .sort(comparatorForSortierung)
      .forEach((key) => sorted.set(key, map.get(key)));
    // fixme es gibt kein .Sortierung auf MInspiration !
    // .forEach((key) => sorted.set(key, map.get(key).sort(comparatorForSortierung)));
    return sorted;
  }

  render() {
    return (
      <>
        <DialogContainer />

        <section
          className="materials fade page-fade-out-on-exit"
          aria-label={I18N("aria::Material- und Zaunartauswahl")}
          data-group-key="Inspirations-Filter"
        >
          {this.hasZaunartSelect() && (
            <fieldset className="zaunart-fieldset">
              <legend className="sr-only">{I18N("aria::Zaunart auswählen")}</legend>
              <Auswahl
                Key="Zaunart"
                type="toggle"
                value={this.state.zaunart}
                auswahlwerte={APP.metadata.Zaunart_Auswahlwert}
                onChange={(zaunart: MAuswahlwert) => {
                  APP.arrangement.zaunart = zaunart;
                  this.setState({ ...this.state, zaunart });
                }}
              />
            </fieldset>
          )}
          <Auswahl
            Key="MaterialKategorie"
            value={this.state.materialKategorie}
            auswahlwerte={this.materialKategorien}
            onChange={(materialKategorie: MAuswahlwert) => {
              this.setState({ ...this.state, materialKategorie });
            }}
          />
        </section>

        <section
          className="tiles fade page-fade-out-on-exit"
          aria-label={I18N("aria::Inspirationsgalerie")}
          data-group-key="Inspirationsgalerie"
        >
          {Array.from(this.getDetailSerieInspirationen()).map(
            ([detailSerie, inspirationen]: [MAuswahlwert, MInspiration[]]) => (
              <InspirationGroup
                key={this.state.zaunart.Key + "_" + detailSerie.Key}
                inspirationen={inspirationen}
                detailSerie={detailSerie}
              />
            )
          )}
        </section>

        <PageButton
          className="weiter-button"
          buttonClassName="primary-button"
          label={I18N("Weiter")}
          pageName={"Zauneditor"}
        />

        <ChatBot
          initialChatMessages={[I18N("TinaBegrüssungstext")]}
          avatarMessage={I18N("TinaInspiration")}
          faqRootKey={"root_Inspiration"}
        />
      </>
    );
  }
}
